import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990PFInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990-PF Instructions & Filing Guide | IRS Requirements"
          description="Learn how to complete Form 990-PF step-by-step, filing deadlines, and options for e-filing or paper filing with ease."
          keywords=""
          canonical="resources/tax-exempt/form-990-pf-instructions/"
          ogType="article"
          jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 990-PF</span>
          </span>{' '}
          Instructions: Filing Guide for Private Foundations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Complete IRS instructions to help private foundations understand and
          file Form 990-PF accurately.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990PFinstructions`}
          >
            E-file Now & Stay Exempt
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-pf-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990-PF at a Glance
                  </Typography>
                  <Typography
                    variant="body1"
                    itemProp="description"
                    sx={{ mb: 2 }}
                  >
                    <Link
                      href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-pf/"
                      sx={{ color: '#0202ff', textDecoration: 'underline' }}
                    >
                      IRS Form 990-PF
                    </Link>{' '}
                    is the annual information return filed by{' '}
                    <b>private foundations</b> and certain{' '}
                    <b>nonexempt charitable trusts</b>. It is used to report
                    financial activities, grants, investments, and compliance
                    with IRS requirements.
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="/resources/tax-exempt/form-990-due-dates/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          <b>Due Date:</b>
                        </Link>{' '}
                        <b>15th day of the 5th month</b> after the end of the
                        foundation&apos;s tax year (May 15 for most
                        calendar-year filers).
                      </span>
                    </li>

                    <li>
                      <span>
                        <Link
                          href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          <b>Extension:</b>
                        </Link>{' '}
                        File <b>IRS Form 8868</b> by the original due date to
                        receive an automatic <b>6-month extension.</b>
                      </span>
                    </li>

                    <li>
                      <span>
                        <Link
                          href="/resources/tax-exempt/form-990-penalties/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          <b>Late Filing Penalties:</b>
                        </Link>{' '}
                        Returns filed late, incomplete, or without the required
                        information may be subject to IRS penalties. Continued
                        failure to meet filing requirements may result in
                        additional compliance actions.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    What is IRS Form 990-PF?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <b>
                      <Link
                        href="/tax-exempt/e-file-irs-form-990-pf/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-PF
                      </Link>
                    </b>
                    ,{' '}
                    <i>
                      Return of Private Foundation or Section 4947(a)(1)
                      Nonexempt Charitable Trust Treated as a Private Foundation
                    </i>
                    , is an annual information return filed by private
                    foundations to the IRS.
                  </Typography>
                  <Typography variant="body1">Filing Form 990-PF is</Typography>
                  <ul style={{ marginBottom: 24 }}>
                    <li style={{ marginBottom: 12 }}>
                      To figure the tax based on investment income, and
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      To report charitable distributions and activities.
                    </li>
                  </ul>
                  <Typography variant="body1" sx={{ mb: 4 }}>
                    The form also provides detailed financial data on private
                    foundations, including their income, expenditures, assets,
                    and grant-making activities.
                  </Typography>
                </Box>

                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        File your 990 series returns fast with TaxZerone and get
                        10% off—use code{' '}
                        <Box
                          component="strong"
                          sx={{
                            ml: 0,
                            color: '#c02652',
                            fontWeight: 600,
                            border: '2px dashed rgb(211, 163, 33)',
                            px: 1,
                            backgroundColor: 'rgb(255, 250, 205)',
                          }}
                        >
                          TZOTE10
                        </Box>{' '}
                        at checkout!
                      </Typography>
                      <Typography variant="body2">
                        We support tax years 2025, 2024, and 2023.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990pfinstructions`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must File Form 990-PF?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 990-PF must be filed by the following foundations
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Section 4947(a)(1) nonexempt charitable trusts treated as
                      private foundations (section 6033(d)).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Exempt private foundations (section 6033(a), (b), and
                      (c)).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Taxable private foundations (section 6033(d)).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Organizations that agree to private foundation status and
                      whose applications for exempt status are pending on the
                      due date for filing Form 990-PF.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Organizations that claim private foundation status,
                      haven&apos;t yet applied for exempt status, and whose
                      application isn&apos;t yet untimely under section 508(a)
                      for retroactive recognition of exemption.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Organizations that made an election under section 41(e)(6)
                      (D)(iv).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Private foundations that are making a section 507(b)
                      termination.
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Section 4947(a)(1) nonexempt charitable trusts
                  that are not treated as private foundations don&apos;t need to
                  file Form 990-PF. They may need to file Form 990 or Form
                  990-EZ.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is Form 990-PF due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Form 990-PF</b> is due on the{' '}
                  <b>15th day of the 5th month</b> after the end of the
                  organization&apos;s tax year. For calendar year filers, this
                  means Form 990-PF is due on <b>May 15th</b> of the following
                  year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, if the due date falls on a weekend or a legal
                  holiday, the filing deadline is extended to the next business
                  day. Foundations can request an automatic 6-month irs
                  extension by filing <br />
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8868
                  </Link>
                  , before the original due date.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  In the case of a complete liquidation, dissolution, or
                  termination, the{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    990 pf
                  </Link>{' '}
                  return must be filed by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    15th day of the 5th month
                  </span>{' '}
                  following complete liquidation, dissolution, or termination.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 990-PF penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If an organization doesn&apos;t file timely and completely or
                  doesn&apos;t furnish the correct information on its 990-PF, it
                  must pay $20 for each day the failure continues ($120 a day if
                  it is a large organization), unless it can show that the
                  failure was due to reasonable cause.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The maximum penalty for each return won&apos;t exceed the
                  smaller of $12,000 ($60,000 for a large organization) or 5% of
                  the gross receipts of the organization for the year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Penalties for Not Paying Tax on Time
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The penalty is generally 1/2 of 1% of the unpaid tax for each
                  month or part of a month the tax remains unpaid, not to exceed
                  25% of the unpaid tax.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The penalty can also be charged if the
                  organization files an incomplete return, such as by failing to
                  complete a required line item or a required part of a
                  schedule.
                </Alert>

                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0,
                        fontSize: '24px !important',
                      }}
                    >
                      Avoid penalties and streamline your tax season!
                    </Typography>
                    <Typography variant="body2">
                      E-file your Form 990-PF with TaxZerone for a hassle-free
                      experience.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 200 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        mb: 0,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990PFinstructions`}
                    >
                      E-file Form 990-PF
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 990-PF Instructions - How to fill out?
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-org-details-img.webp" />
                  <source srcSet="/assets/images/f990pf-org-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-org-details-img.png"
                    loading="lazy"
                    alt="Organization details of 990-pf"
                    width={760}
                    height={327}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 990-PF.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section requires information about your foundation,
                  including name, address, accounting method, tax-exempt status,
                  and tax period.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Keeping the previous year&apos;s filed 990-PF return in hand
                  can help to complete your filing process.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item A - Employer identification number
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This section collects information about your
                  organization&apos;s EIN. If your organization has more than
                  one EIN, notify the Internal Revenue Service and they will
                  advise which number to use in the return.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item B - Telephone Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This section requires your foundation’s telephone number
                  (including the area code) that the public and government
                  regulators may use to obtain information about the
                  foundation&apos;s finances and activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Item C</span> - Check this
                  checkbox if your exemption application is pending
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item D - Foreign Organizations
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    If your foundation is a foreign organization, then Check the
                    box in D1 in the Heading section on page 1 of Form 990-PF.
                    Also, the foundation must not complete Part IV or Part I,
                    line 7.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If the foreign organization meets the 85% test of
                    Regulations section 53.4948-1(b), then:
                  </li>
                </ol>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Check the box in D2 in the Heading section on page 1 of Form
                    990-PF,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Check the box at the top of Part X,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t fill in Parts X and XII,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t fill in Part IX unless it is claiming status as a
                    private operating foundation, and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Attach the computation of the 85% test to Form 990-PF
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item E - Section 507(b)(1)(A) Terminations
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your private foundation status was terminated under section
                  507(b)(1)(A), then check the Item E box in the Heading section
                  on page 1 of Form 990-PF.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item F - 60-Month Termination Under Section 507(b)(1)(B)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your private foundation is terminating its private
                  foundation status under the 60-month provisions of section
                  507(b)(1)(B) during the period covered by this return, then
                  check the Item F box in the Heading section on page 1 of Form
                  990-PF.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item G - Applicable Checkboxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the appropriate boxes to indicate changes on the current
                  return compared with the previously filed 990-PF return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Initial Return</b> - Check the box if the foundation is
                  filing a Form 990-PF return for the first time and hasn&apos;t
                  previously filed a{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-EZ
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-T
                  </Link>
                  , or{' '}
                  <Link
                    href="https://epostcard.taxzerone.com/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    990-N
                  </Link>{' '}
                  return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Initial Return of a Former Public Charity</b> - Check the
                  box if the foundation is filing a Form 990-PF return for the
                  first time as a public charity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Final Return</b> - Check the box if the foundation has
                  terminated its existence or ceased to be a Private Foundation
                  and is filing its final return.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Amended Return</b> - Check the box if the foundation is
                  filing an amended return for the tax year to amend the
                  previously filed return for the same tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must also attach Schedule O, explain which parts,
                  schedules, or attachments of Form 990-PF were amended, and
                  describe those amendments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Address Change</b> - Check the box if the foundation
                  changed its address and didn&apos;t report the change on the
                  previously filed Form 990-PF.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Name Change</b> - Check the box if the foundation changed
                  its legal name (not its “DBA” name) and didn&apos;t report the
                  change on the previously filed 990-PF return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must also attach <b>supporting documents</b> for the name
                  change based on your foundation&apos;s type.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item H - Type of Organization
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Check the box for “
                    <b>Section 501(c)(3) exempt private foundation</b>” if the
                    foundation has a ruling or determination letter from the IRS
                    in effect that recognizes its exemption from federal income
                    tax as an organization described in section 501(c)(3) or if
                    the organization&apos;s exemption application is pending
                    with the IRS.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Check the “
                    <b>Section 4947(a)(1) nonexempt charitable trust</b>” box if
                    the trust is a nonexempt charitable trust treated as a
                    private foundation.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If your foundation doesn&apos;t come under the first two
                    foundation types, check the “
                    <b>Other taxable private foundation</b>” box.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item I - Fair Market Value of All Assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this section, enter the fair market value of all assets the
                  foundation held at the end of the tax year.
                </Typography>

                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  This amount should be the same as the figure reported in Part
                  II, line 16, column (c).
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item J - Accounting method
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select the accounting method the organization used to complete
                  its Form 990-PF return. It can be Cash, Accrual, or Other.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If the organization changed its method of accounting from a
                  prior year or checked “Other,” explain the change on{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-schedule-o/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule O (Form 990)
                  </Link>
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part I - Analysis of Revenue and Expenses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part1-line1-12-img.webp" />
                  <source srcSet="/assets/images/f990pf-part1-line1-12-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part1-line1-12-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part I Lines 1–12 revenue and expense summary"
                    width={760}
                    height={305}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - Enter the
                  total gross contributions, gifts, grants, and similar amounts
                  your organization received for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Check the
                  checkbox in line 2 if the foundation isn&apos;t required to
                  attach Schedule B.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3 - Interest on savings and temporary cash investments.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (a) - Enter the total amount of interest income
                    from investments reportable in Part II, line 2. These
                    include savings or other interest-bearing accounts and
                    temporary cash investments, such as money market funds,
                    commercial paper, certificates of deposit, and U.S. Treasury
                    bills or other government obligations that mature in less
                    than 1 year.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (b) - Enter the amount of interest income shown in
                    column (a). Don&apos;t include interest on tax-exempt
                    government obligations.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (c) - Enter the amount of interest income shown in
                    column (a). Include interest on tax-exempt government
                    obligations.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4 - Dividends and interest from securities
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (a). Enter the amount of dividend and interest
                    income from securities (stocks and bonds) reportable in Part
                    II, line 10. Include amounts received from payments on
                    securities loans, as defined in section 512(a)(5).
                    Don&apos;t include any capital gain dividends reportable on
                    line 6a. Report income from program-related investments on
                    line 11. For debt instruments with an original issue
                    discount, report the original issue discount ratably over
                    the life of the bond on line 4. See section 1272 for more
                    information.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (b). Enter the amount of dividend and interest
                    income and payments on securities loans from column (a).
                    Don&apos;t include interest on tax-exempt government
                    obligations.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (c). Enter the amount of dividend and interest
                    income and payments on securities loans from column (a).
                    Include interest on tax-exempt government obligations.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5a - Gross rents
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (a). Enter the gross rental income for the year
                    from investment property reportable in Part II, line 11.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In columns (b) and (c). Enter the gross rental income from
                    column (a).
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5b - Net rental income or (loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Figure the net rental income or (loss) for the year and enter
                  that amount on the entry line to the left of column (a).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, report rents from other sources on line 11. Enter any
                  expenses attributable to the rental income reported on line 5,
                  such as interest and depreciation, on lines 13–23.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 6a</span> - Enter here
                  the net gain or (loss) per book from all asset sales not
                  included in line 10.
                </Typography>
                <Typography variant="body1">
                  For assets sold and not included in Part IV, attach a schedule
                  showing:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>Date acquired;</li>
                  <li style={{ marginBottom: 12 }}>Manner of acquisition;</li>
                  <li style={{ marginBottom: 12 }}>Gross sales price;</li>
                  <li style={{ marginBottom: 12 }}>
                    Cost, other basis, or value at time of acquisition (if
                    donated) and which of these methods was used;
                  </li>
                  <li style={{ marginBottom: 12 }}>Date sold;</li>
                  <li style={{ marginBottom: 12 }}>To whom sold;</li>
                  <li style={{ marginBottom: 12 }}>
                    Expense of sale and cost of improvements made after
                    acquisition; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Depreciation since acquisition (if depreciable property).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 6b</span> - Enter in
                  this line the gross sales price from all asset sales whose net
                  gain or loss was reported on line 6a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 7</span> - Enter in
                  line 7 the capital gain net income from Part IV, line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 8</span> - Enter only
                  the net short-term capital gain for the year (assets sold or
                  exchanged that were held not more than 1 year). Don&apos;t
                  include net long-term capital gain or net loss in column (c).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Don&apos;t include the net gain from the sale or exchange of
                  depreciable property, or land used in a trade or business
                  (section 1231) and held for more than 1 year. However, include
                  net loss from such property on line 23 as an{' '}
                  <b>
                    <i>Other expense</i>
                  </b>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Income modifications.
                </Typography>
                <Typography variant="body1">Include on this line:</Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Amounts received or accrued as repayments of amounts taken
                    into account as qualifying distributions;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Amounts received or accrued from the sale or other
                    disposition of property to the extent that the acquisition
                    of the property was considered a qualifying distribution for
                    any tax year;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any amount set aside for a specific project (see explanation
                    in the instructions for Part XI) that wasn&apos;t necessary
                    for the purposes for which it was set aside;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Income received from an estate, but only if the estate was
                    considered terminated for income tax purposes due to a
                    prolonged administration period; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Amounts treated in an earlier tax year as qualifying
                    distributions to:
                  </li>
                </ol>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 10a, b, c - Gross profit from sales of inventory
                </Typography>
                <Typography variant="body1">
                  Enter in line 10 the gross sales (less returns and
                  allowances), cost of goods sold, and gross profit or (loss)
                  from the sale of all inventory items, including those sold in
                  the course of special events and activities. These inventory
                  items are the ones the organization either makes to sell to
                  others or buys for resale.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t report any sales or exchanges of investments on
                    line 10.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t include any profit or (loss) from the sale of
                    capital items such as securities, land, buildings, or
                    equipment on line 10. Enter these amounts on line 6a.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t include any business expenses such as salaries,
                    taxes, rent, etc., on line 10. Include them on lines 13–23.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach a schedule showing the following items: gross sales,
                  cost of goods sold, and gross profit or (loss). These items
                  should be classified according to the type of inventory sold
                  (such as books, tapes, other educational or religious
                  material, etc.). The totals from the schedule should agree
                  with the entries on lines 10a– 10c.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In column (c), enter the gross profit or (loss) from sales of
                  inventory shown on line 10c, column (a).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11- Other income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total of all the foundation&apos;s Other Income for
                  the year. Attach a schedule that gives a description and the
                  amount of the income. Include all income not reported on lines
                  1 through 10c.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Don&apos;t include unrealized gains and losses on investments
                  carried at market value. Report those as fund balance or net
                  asset adjustments in Part III.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (b). Enter the amount of investment income
                    included in line 11, column (a). Include dividends,
                    interest, rents, and royalties derived from assets devoted
                    to charitable activities, such as interest on student loans.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (c). Include all other items includible in
                    adjusted net income not covered elsewhere in column (c).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12</span> - Add lines
                  1–11 in columns (a)–(c) and enter the total value in line 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In column (b). Domestic organizations should enter the total
                  of lines 3–11. Tax-exempt foreign foundations should exclude
                  the line 7 amount from the total.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13 - Compensation of officers, directors, trustees, etc.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part1-line13-26-img.webp" />
                  <source srcSet="/assets/images/f990pf-part1-line13-26-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part1-line13-26-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part I Lines 13–26 operating and administrative expenses"
                    width={760}
                    height={265}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (a). Enter the total compensation for the year of
                    all officers, directors, and trustees. If none was paid,
                    enter zero. Complete line 1 of Part VII to show the
                    compensation of officers, directors, trustees, and
                    foundation managers.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In columns (b), (c), and (d). Enter the portion of the
                    compensation included in column (a) that applies to the
                    column. For example, in column (c), enter the portion of the
                    compensation included in column (a) paid or incurred to
                    produce or collect income included in column (c).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 14</span> - In line 14,
                  enter the salaries and wages of all employees other than those
                  included in line 13.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 15</span> - Enter the
                  employer&apos;s share of contributions the organization paid
                  to qualified and nonqualified pension plans and the
                  employer&apos;s share of contributions to employee benefit
                  programs (such as insurance, health, and welfare programs)
                  that aren&apos;t an incidental part of a pension plan.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Also include the amount of federal, state, and local payroll
                  taxes for the year, but only include those that are imposed on
                  the organization as an employer. This includes the
                  employer&apos;s share of social security and Medicare taxes,
                  FUTA tax, state unemployment compensation tax, and other state
                  and local payroll taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Don&apos;t include taxes withheld from employees&apos;
                  salaries and paid over to the various governmental units (such
                  as federal and state income taxes and the employee&apos;s
                  share of social security and Medicare taxes).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 16a, b, and c - Legal, accounting, and other
                  professional fees
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  On the appropriate line(s), enter the legal, accounting,
                  auditing, and other professional fees (such as fees for
                  fundraising or investment services) charged by outside firms
                  and individuals who aren&apos;t employees of the foundation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, report any fines, penalties, or judgments imposed
                  against the foundation as a result of legal proceedings on
                  line 23.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18 - Taxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach a schedule listing the type and amount of each tax
                  reported on line 18. Don’t enter any taxes included on line
                  15.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (a). Enter the taxes paid (or accrued) during the
                    year. Include all types of taxes recorded on the books,
                    including real estate tax not reported on line 20, the tax
                    on investment income, and any income tax.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (b). Enter only those taxes included in column (a)
                    related to investment income taxable under section 4940.
                    Don&apos;t include the Section 4940 tax paid or incurred on
                    net investment income or the Section 511 tax on unrelated
                    business income. Sales taxes may not be deducted separately
                    but must be treated as a part of the cost of acquired
                    property or as a reduction of the amount realized on the
                    disposition of the property.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (c). Enter only those taxes included in column (a)
                    that relate to income included in column (c). Don&apos;t
                    include any excise tax paid or incurred on the net
                    investment income (as shown in Part V) or any tax reported
                    on Form 990-T.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (d). Don&apos;t include any excise tax paid on
                    investment income (as reported in Part V of this return or
                    the equivalent part of a return for prior years) unless the
                    organization is claiming status as a private operating
                    foundation and completes Part XIII.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 19 - Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In column (a). Enter the expenses recorded in the books for
                  the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For depreciation, attach a schedule showing:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    A description of the property,
                  </li>
                  <li style={{ marginBottom: 12 }}>The date acquired,</li>
                  <li style={{ marginBottom: 12 }}>
                    The cost or other basis (exclude any land),
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The depreciation allowed or allowable in prior years,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The method of computation,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The rate (%) or life (years), and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The depreciation this year.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  On a separate line on the schedule, show the amount of
                  depreciation included in the cost of goods sold and not
                  included on line 19.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In columns (b) and (c). A depreciation deduction is allowed
                  only for property used in the production of income reported in
                  the column, and only using the straight-line method of
                  figuring depreciation. A deduction for depletion is allowed
                  but must be figured only using the cost depletion method.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 20</span> - Enter the
                  amount paid or incurred for the use of office space or other
                  facilities.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    If the space is rented or leased, enter the amount of rent.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If the space is owned, enter the amount of mortgage
                    interest, real estate taxes, and similar expenses, but not
                    depreciation reportable on line 19
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In either case, include the amount for utilities and related
                  expenses (for example, heat, lights, water, power, telephone,
                  sewer, trash removal, outside janitorial services, and similar
                  services). Don&apos;t include any salaries of the
                  organization&apos;s employees reportable on line 14.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 21</span> - Enter the
                  expenses for officers, employees, or others during the year
                  for travel, attending conferences, meetings, etc.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include transportation (including fares, mileage allowance, or
                  automobile expenses), meals and lodging, and related costs
                  whether paid based on a per diem allowance or actual expenses
                  incurred. However, don&apos;t include any compensation paid to
                  those who participate.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (b). Only 50% of the expense for business meals
                    paid or incurred in connection with travel, meetings, etc.,
                    relating to the production of investment income may be
                    deducted in figuring net investment income (section 274(n)).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (c). Subject to the Special rule, earlier,
                    limiting amounts reported in column (c) by the income
                    generated by a charitable activity, enter the total amount
                    of expenses paid or incurred by officers, employees, or
                    others for travel, conferences, meetings, etc., related to
                    income included in column (c).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 22</span> - Enter in
                  this line the expenses for printing publishing and
                  distributing any newsletters, magazines, etc. Also include the
                  cost of subscriptions to, or purchases of, magazines,
                  newspapers, etc.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 23</span> - Enter all
                  other expenses for the year. Include all expenses not reported
                  on lines 13–22.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If a deduction is claimed for amortization, attach a schedule
                  showing:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Description of the amortized expenses;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Date acquired, completed, or expended;
                  </li>
                  <li style={{ marginBottom: 12 }}>Amount amortized;</li>
                  <li style={{ marginBottom: 12 }}>
                    Deduction for prior years;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Amortization period (number of months);
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Current-year amortization; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Total amount of amortization.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 24 - Total operating and administrative expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add lines 13 through 23 and enter the sum amount in line 24.
                  This amount will be the foundation’s total operating and
                  administrative expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 25</span> - Don’t
                  report on line 25 direct program expenditures that aren&apos;t
                  contributions, gifts, or grants. These amounts should be
                  reported on lines 13– 24.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In column (a). Enter the total of all contributions, gifts,
                  grants, and similar amounts paid (or accrued) for the year.
                  List each contribution, gift, grant, etc., in Part XIV, or
                  attach a schedule of the items included on line 25 and list:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Name and address of donee;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Relationship of donee if related by:
                  </li>
                  <ol style={{ listStyleType: 'lower-alpha' }}>
                    <li style={{ marginBottom: 12 }}>Blood,</li>
                    <li style={{ marginBottom: 12 }}>Marriage, ,</li>
                    <li style={{ marginBottom: 12 }}>Adoption, or</li>
                    <li style={{ marginBottom: 12 }}>
                      Employment (including children of employees) to any
                      disqualified person
                    </li>
                  </ol>
                </ol>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 26 - Total expenses and disbursements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add lines 24 and 25 and enter the sum in this line. The sum
                  amount will be the foundation&apos;s{' '}
                  <b>total expenses and disbursements</b> for the tax year.
                  administrative expenses.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 27a - Excess of revenue over expenses and disbursements.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part1-line27-img.webp" />
                  <source srcSet="/assets/images/f990pf-part1-line27-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part1-line27-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part I expenses & Line 27 income calculations"
                    width={760}
                    height={65}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 26, column (a), from line 12, column (a), and
                  enter the difference in this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 27b - Net investment income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Domestic organizations should subtract line 26, column (b),
                  from line 12, column (b), and enter the difference. Exempt
                  foreign organizations should enter the amount shown on line
                  12, column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, if the organization is a domestic organization and
                  line 26, column (b), is more than line 12, column (b) (such as
                  when expenses exceed income), enter zero (not a negative
                  amount).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 27c - Adjusted net income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 26, column (c), from line 12, column (c), and
                  enter the difference in this line.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part II - Balance Sheets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partII-assets-img.webp" />
                  <source srcSet="/assets/images/f990pf-partII-assets-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partII-assets-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part II assets"
                    width={760}
                    height={444}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Foundations whose books of account included total assets of
                  $5,000 or more at any time during the year must complete all
                  columns (a), (b), and (c).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Foundations with less than $5,000 of total assets per book at
                  all times during the year must complete all of the columns (a)
                  and (b) and only line 16 of column (c).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - Enter the
                  amount of cash on deposit in checking accounts, deposits in
                  transit, change funds, petty cash funds, and any other
                  non-interest-bearing account.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t include advances to employees or officers
                  or refundable deposits paid to suppliers or others.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Enter in
                  this line the total of cash in savings or other
                  interest-bearing accounts and temporary cash investments, such
                  as money market funds, commercial paper, certificates of
                  deposit, and U.S. Treasury bills or other governmental
                  obligations that mature in less than 1 year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - In column
                  (a), enter the year-end figures for total accounts receivable
                  and allowance for doubtful accounts from the sale of goods
                  and/or the performance of services. In columns (a), (b), and
                  (c), enter net amounts (total accounts receivable reduced by
                  the corresponding allowance for doubtful accounts).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - In column
                  (a), enter the year-end figures for total pledges receivable
                  and allowance for doubtful accounts (pledges estimated to be
                  uncollectible). In columns (a), (b), and (c), enter net
                  amounts (total pledges receivable reduced by the corresponding
                  allowance for doubtful accounts)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Enter the
                  total grants receivable from governmental agencies,
                  foundations, and other organizations as of the beginning and
                  end of the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 6</span> - Enter in
                  this line (and on an attached schedule described below) all
                  receivables due from officers, directors, trustees, foundation
                  managers, and other disqualified persons and all secured and
                  unsecured loans (including advances) to such persons.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Attached schedules.
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    On the required schedule, report each loan separately, even
                    if more than one loan was made to the same person or the
                    same terms apply to all loans made.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Receivables that are subject to the same terms and
                    conditions (including credit limits and rate of interest) as
                    receivables due from the general public from an activity
                    functionally related to the foundation&apos;s charitable
                    purposes may be reported as a single total for all the
                    officers, directors, etc.
                  </li>
                </ol>
                <Typography variant="body1">
                  For each outstanding loan or other receivable that must be
                  reported separately, the attached schedule should show the
                  following information (preferably using columns).
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Borrower&apos;s name and title.
                  </li>
                  <li style={{ marginBottom: 12 }}>Original amount.</li>
                  <li style={{ marginBottom: 12 }}>Balance due.</li>
                  <li style={{ marginBottom: 12 }}>Date of note.</li>
                  <li style={{ marginBottom: 12 }}>Maturity date.</li>
                  <li style={{ marginBottom: 12 }}>Repayment terms.</li>
                  <li style={{ marginBottom: 12 }}>Interest rate.</li>
                  <li style={{ marginBottom: 12 }}>
                    Security provided by the borrower.
                  </li>
                  <li style={{ marginBottom: 12 }}>Purpose of the loan.</li>
                  <li style={{ marginBottom: 12 }}>
                    Description and fair market value of the consideration
                    furnished by the lender (for example, cash—$1,000; or 100
                    shares of XYZ, Inc., common stock— $9,000)
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t adjust the amounts reported by any
                  amount(s) estimated to be uncollectible.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7</span> - Enter the
                  combined total year-end figures for other notes receivable and
                  loans receivable and the allowance for doubtful accounts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Notes receivable</b>. In columns (a), (b), and (c), enter
                  the amount of all notes receivable not listed on line 6 and
                  not acquired as investments. Attach a schedule similar to the
                  one for line 6. The schedule should also identify the
                  relationship of the borrower to any officer, director,
                  trustee, foundation manager, or other disqualified person.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Loans receivable</b>. In columns (a), (b), and (c), enter
                  the gross amount of loans receivable, minus the allowance for
                  doubtful accounts, from the normal activities of the filing
                  organization (such as scholarship loans). An itemized list of
                  these loans isn&apos;t required, but attach a schedule showing
                  the total amount of each type of outstanding loan.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 8</span> - Enter in
                  this line the amount of materials, goods, and supplies
                  purchased or manufactured by the organization and held for
                  sale or use in some future period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 9</span> - Enter the
                  amount of short-term and long-term prepayments of expenses
                  attributable to one or more future accounting periods.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Examples include prepayments of rent, insurance, and pension
                  costs, and expenses incurred in connection with a solicitation
                  campaign to be conducted in a future accounting period.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 10a, b, and c. Investments— government obligations,
                  corporate stock and bonds
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the book value (which may be the market value) of these
                  investments. Attach a schedule that lists each security held
                  at the end of the year and shows whether the security is
                  listed at cost (including the value recorded at the time of
                  receipt in the case of donated securities) or end-of-year
                  market value.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    In column (a), enter the year-end book value (excluding
                    accumulated depreciation), and on the second dashed line,
                    enter the accumulated depreciation of all land, buildings,
                    and equipment held for investment purposes, such as rental
                    properties.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In columns (a) and (b), enter the book value of all land,
                    buildings, and equipment held for investment less
                    accumulated depreciation.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    In column (c), enter the fair market value of these assets.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Attach a schedule listing these investment fixed assets held
                  at the end of the year and showing, for each item or category
                  listed, the original cost or other basis, accumulated
                  depreciation, and ending book value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 12</span> - Enter in
                  this line the amount of mortgage loans receivable held as
                  investments but don&apos;t include program-related
                  investments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 13</span> - Enter here
                  the amount of all other investment holdings not reported on
                  lines 10 through 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, attach a schedule listing and describing each of these
                  investments held at the end of the year. Show the book value
                  for each and indicate whether the investment is listed at cost
                  or end-of-year market value. Don’t include program-related
                  investments (see the instructions for line 15).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 14</span> - In columns
                  (a) and (b), enter the book value of all land, buildings, and
                  equipment not held for investment less accumulated
                  depreciation. In column (c), enter the fair market value of
                  these assets. Include any property, plant, and equipment owned
                  and used by the organization to conduct its charitable
                  activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Attach a schedule listing these fixed assets held at the end
                  of the year and showing the original cost or other basis,
                  accumulated depreciation, and ending book value of each item
                  or category listed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 15</span> - In this
                  line, list the book value of each category of assets not
                  reportable on lines 1 through 14.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  One type of asset reportable on line 15 is program-related
                  investments. These are investments made primarily to
                  accomplish a charitable purpose of the filing organization
                  with no significant purpose to produce income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 16</span> - All filers
                  must complete line 16 of columns (a), (b), and (c). These
                  entries represent the totals of lines 1 through 15 of each
                  column.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, foundations that have assets of less than $5,000 per
                  book at all times during the year need not complete lines 1
                  through 15 of column (c).
                </Typography>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The column (c) amount is also entered in the
                  entry space for Item I in the Heading section on page 1.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990pf-partII-line17-23-img.webp" />
                  <source srcSet="/assets/images/f990pf-partII-line17-23-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partII-line17-23-img.png"
                    loading="lazy"
                    alt=" Form 990-PF Part II Lines 17–23 liabilities"
                    width={760}
                    height={128}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 17</span> - Enter in
                  line 17 the total of accounts payable to suppliers and others
                  and accrued expenses, such as salaries payable, accrued
                  payroll taxes, and interest payable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 18</span> - Enter here
                  the unpaid portion of grants and awards the organization has
                  committed to pay other organizations or individuals, whether
                  or not the commitments have been communicated to the grantees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 19</span> - Include in
                  this line revenue that your organization has received but not
                  yet earned as of the balance sheet date under its method of
                  accounting.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 20</span> - Enter here
                  the unpaid balance of loans received from officers, directors,
                  trustees, and other disqualified persons.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If loans are outstanding at the end of the year, attach a
                  schedule that shows (for each loan) the name and title of the
                  lender and the information listed in items 2 through 10 of the
                  instructions for line 6, earlier.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 21</span> - Enter the
                  amount of mortgages and other notes payable at the beginning
                  and end of the year. Also, attach a schedule showing, as of
                  the end of the year, the total amount of all mortgages payable
                  and, for each nonmortgage note payable, the name of the lender
                  and the other information specified in items 2 through 10 of
                  the instructions for line 6, earlier.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 22</span> - In line 22,
                  list the amount of each liability not reportable on lines 17
                  through 21. You can attach a separate schedule if more space
                  is needed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 23</span> - Add lines
                  17 through 22 and enter the sum in this line. The total amount
                  is the Total liabilities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 24 - 30. Net Assets or Fund Balances
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partII-line24-30-img.webp" />
                  <source srcSet="/assets/images/f990pf-partII-line24-30-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partII-line24-30-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part II Lines 24-30 Net Assets or Fund Balances"
                    width={760}
                    height={212}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0,
                        fontSize: '16px !important',
                      }}
                    >
                      If you&apos;re a foundation that follows FASB ASC 958,
                      check the checkbox above line 24 and complete lines 24,
                      25, 29, and 30.
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 24</span> - Enter here
                  the balances per book of the net assets without donor
                  restrictions class of net assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>
                    <i> Net assets without donor restrictions</i>
                  </b>{' '}
                  are neither permanently restricted nor temporarily restricted
                  by donor-imposed stipulations. All funds without donor-imposed
                  restrictions must be classified as net assets without donor
                  restrictions, regardless of the existence of any board
                  designations or appropriations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 25</span> - The
                  organization must use this line to show the balance per books
                  of net assets with donor-imposed restrictions that may require
                  resources to be used after a specified date (time
                  restrictions), used for a specified purpose (purpose
                  restrictions), or both.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0,
                        fontSize: '16px !important',
                      }}
                    >
                      If you&apos;re not a foundation that follows FASB ASC 958,
                      check the checkbox above line 26 and complete lines 26
                      through 30
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 26 - Capital stock, trust principal, or current funds
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    For corporations, enter the balance per book for capital
                    stock accounts. Show par or stated value (or for stock with
                    no par or stated value, the total amount received upon
                    issuance) of all classes of stock issued and, as yet,
                    uncanceled.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For trusts, enter the amount in the trust principal or
                    corpus account.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For foundations continuing to use the fund method of
                    accounting, enter the fund balances for the
                    foundation&apos;s current restricted and unrestricted funds
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 27</span> - Enter in
                  line 27 the balance per book for all paid-in capital over par
                  or stated value for all stock issued and uncanceled. If
                  stockholders or others gave donations that the organization
                  records as paid-in capital, include them here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any current-year donations you included on line 27 in
                  Part I, line 1. The fund balance for the land, building, and
                  equipment fund would be entered here.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 28 - Retained earnings, accumulated income, endowment, or
                  other funds.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    For corporations, enter the balance in the retained
                    earnings, or similar account, minus the cost of any
                    corporate treasury stock.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For trusts, enter the balance per book in the accumulated
                    income or similar account.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For foundations using fund accounting, enter the total of
                    the fund balances for the permanent and term endowment funds
                    as well as balances of any other funds not reported on lines
                    26 and 27
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 29 - Total net assets or fund balances
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For foundations that follow FASB ASC 958, enter the total of
                  lines 24 and 25. For all other foundations, enter the total of
                  lines 26 through 28
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Enter the beginning-of-year figure in Part III,
                  line 1. The end-of-year figure in column (b) must agree with
                  the figure in Part III, line 6.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 30 - Total liabilities and net assets/fund balances
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total of lines 23 and 29. This amount must equal the
                  amount for total assets reported on line 16 for both the
                  beginning and end of the year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part III - Analysis of Changes in Net Assets or Fund Balances
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partIII-line1-6-img.webp" />
                  <source srcSet="/assets/images/f990pf-partIII-line1-6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partIII-line1-6-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part III, Lines 1–6 showing analysis of changes in net assets or fund balances, including excess of revenue over expenses, net investment income, and year-end adjustments"
                    width={760}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - In this
                  line, the total net assets or fund balances at the beginning
                  of the year—Part II, column (a), line 29 (must agree with the
                  end-of-year figure reported on the prior year&apos;s return)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - In this
                  line, re-enter the amount from Part I, line 27(a), column (a).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Enter here
                  the increase in net assets that wasn&apos;t included in line
                  2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Add lines 1,
                  2, and 3 and enter the sum in line 4.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Enter the
                  net asset decrease not included in line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 6</span> - Calculate
                  Total net assets or fund balances at the end of the year (line
                  4 minus line 5)—Part II, column (b), line 29 and enter the
                  value in line 6.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part IV - Capital Gains and Losses for Tax on Investment
                  Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partIV-line1-3-img.webp" />
                  <source srcSet="/assets/images/f990pf-partIV-line1-3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partIV-line1-3-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part IV, Lines 1–3 showing capital gains and losses used to calculate tax on investment income "
                    width={760}
                    height={446}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  As a private foundation, you can use Part IV to figure the
                  amount of net capital gain to report on lines 7 and 8 of Part
                  I. This part doesn&apos;t apply to foreign organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - Publicly
                  traded securities are securities that are listed and regularly
                  traded on an over-the-counter market or an established
                  exchange in which market quotations are published or otherwise
                  readily available. Securities include
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Common and preferred stock,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Bonds (including governmental obligations), and
                  </li>
                  <li style={{ marginBottom: 12 }}>Mutual fund shares.</li>
                </ul>
                <ol>
                  <li style={{ marginBottom: 12 }}>
                    For sales of publicly traded securities through a broker,
                    enter the description “publicly traded securities” on line
                    1, column (a).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Leave columns (b), (c), and (d) blank.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Total the gross sales price, the cost or other basis, and
                    the expense of sale on all such securities sold. Report
                    these lump-sum figures in columns (e) through (l), as
                    appropriate
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must maintain detailed records of each transaction in your
                  books and records.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Other gains and losses</b> - For sales of anything other
                  than publicly traded securities sold, each transaction must be
                  listed and reported separately, completing all appropriate
                  columns in Part IV.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Capital gain net income or (net capital loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the value is gain, enter in Part I, line 7. If (loss),
                  enter -0- in Part I, line 7
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3 - Net short-term capital gain or (loss) as defined in
                  sections 1222(5) and (6)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the value is gained, enter in Part I, line 8, column (c).
                  See instructions. If (loss), enter -0- in Part I, line 8.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part V - Excise Tax Based on Investment Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partV-line1-11-img.webp" />
                  <source srcSet="/assets/images/f990pf-partV-line1-11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partV-line1-11-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part V Lines 1–11 excise tax on investment income"
                    width={760}
                    height={325}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    Line 1a. Domestic exempt private foundations
                  </span>{' '}
                  - If your foundation is an exempt operating foundation
                  described in section 4940(d)(2), check the checkbox in this
                  line and enter “N/A”
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    Line 1b. Domestic taxable private foundations and section
                    4947(a) (1) nonexempt charitable trusts
                  </span>{' '}
                  - IAll other domestic foundations enter 1.39% (0.0139) of line
                  27b. Exempt foreign organizations, enter 4% (0.04) of Part I,
                  line 12, col. (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - If your
                  organization comes under section 4940(b), a domestic section
                  4947(a)(1) nonexempt charitable trust or taxable private
                  foundation, it must add to the tax figured under section
                  4940(a) (on line 1) the tax which would have been imposed
                  under section 511 for the tax year if it had been exempt from
                  tax under section 501(a).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Domestic
                  section 4947(a)(1) nonexempt charitable trusts and taxable
                  private foundations, enter the amount of subtitle A (income)
                  tax for the year reported on Form 1041 or Form 1120. All other
                  filers, enter zero.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Credits and Payments
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6a</span> - Enter in
                  this line the amount of 2025 estimated tax payments and any
                  2024 overpayment of taxes that the organization specified on
                  its 2024 return to be credited toward payment of 2025
                  estimated taxes.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note : </b>Line 6a applies only to domestic foundations.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6b</span> - Exempt
                  foreign foundations must enter the amount of tax withheld at
                  the source here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6c</span> - Enter this
                  line the tax paid with the application for an extension of
                  time to file (Form 8868).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 6d</span> - Enter here
                  the amount of any backup withholding erroneously withheld.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 7</span> - Add lines 6a
                  through 6d and enter the total in this line. This will be the
                  Total credits and payments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 8</span> - Enter in
                  this line any penalty for underpayment of estimated tax shown
                  on Form 2220.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 9</span> -{' '}
                  <b>Tax due</b>. If the sum of lines 5 and 8 is more than line
                  7, enter the amount owed in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 10</span> -{' '}
                  <b>Overpayment</b>. If line 7 is more than the total of lines
                  5 and 8, enter the amount overpaid in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11</span> - Enter here
                  the amount of line 10 to be: Credited to the 2026 estimated
                  tax amount.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 3,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> If your
                  organization is entitled to a refund when filing Form 990-PF,
                  you may request to receive the refund through direct deposit
                  by completing and attaching Form 8050. For detailed guidance
                  on completing the form, visit our{' '}
                  <Link
                    href="/resources/tax-exempt/form-8050-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8050 instructions
                  </Link>{' '}
                  page.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VI-A Statements Regarding Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVI-A-line1-10-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVI-A-line1-10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVI-A-line1-10-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VI-A Lines 1–10 foundation activities & compliance questions "
                    width={760}
                    height={543}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - Select
                  “Yes” if the foundation attempted to influence any national,
                  state, or local legislation or participated or intervened in
                  any political campaign.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - Select
                  “Yes” if the foundation spent more than $100 during the year
                  (either directly or indirectly) for political purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the answer is “Yes” to 1a or 1b, attach a detailed
                  description of the activities and copies of any materials
                  published or distributed by the foundation in connection with
                  the activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1c</span> - Select
                  “Yes” if the foundation filed Form 1120-POL for this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1d</span> - Enter the
                  amount (if any) of tax on political expenditures (section
                  4955) imposed during the year for both the foundation and
                  foundation managers.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 1e</span> - Enter in
                  this line the reimbursement (if any) paid by the foundation
                  during the year for political expenditure tax imposed on
                  foundation managers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Select “Yes”
                  if the foundation engaged in any activities that have not
                  previously been reported to the IRS. If “Yes,” attach a
                  detailed description of the activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Select “Yes”
                  if the foundation made any changes, not previously reported to
                  the IRS, in its governing instrument, articles of
                  incorporation, bylaws, or other similar instruments. If “Yes,”
                  attach a conformed copy of the changes.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 0,
                        fontSize: '16px !important',
                        lineHeight: 1.3,
                      }}
                    >
                      A “conformed copy” of an organizational document agrees
                      with the original document and all its amendments. If
                      copies aren&apos;t signed, attach a written declaration
                      signed by an officer authorized to sign for the
                      organization, certifying that they are complete and
                      accurate copies of the original documents.
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4a</span> - Select
                  “Yes” if the foundation has an unrelated business gross income
                  of $1,000 or more during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4b</span> - If the
                  foundation selected “Yes” to line 4a, select Yes in this line
                  if it filed a tax return on Form 990-T for this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Select “Yes”
                  if there was a liquidation, termination, dissolution, or
                  substantial contraction during the year. If selected “Yes,”
                  attach the statement required by General Instruction T.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 6</span> - Select “Yes”
                  if the requirements of section 508(e) (relating to sections
                  4941 through 4945) satisfied either:
                </Typography>
                <ul style={{ marginBottom: 16 }}>
                  <li style={{ marginBottom: 12 }}>
                    By language in the governing instrument, or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    By state legislation that effectively amends the governing
                    instrument so that no mandatory directions that conflict
                    with the state law remain in the governing instrument?
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Line 6 doesn&apos;t apply to foreign foundations
                  described in section 4948(b).
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 7</span> - Select “Yes”
                  if the foundation has at least $5,000 in assets at any time
                  during the year. If selected “Yes,” the foundation must
                  complete Part II, col. (c), and Part XIV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 8a</span> - Enter in
                  this line the states
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    To which the organization reports in any way about its
                    organization, assets, or activities; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    With which the organization has registered (or which it has
                    otherwise notified in any manner) that it intends to be, or
                    is a charitable organization or that it is, or intends to
                    be, a holder of property devoted to a charitable purpose.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Attach a separate list if you need more space. Line 8
                  doesn&apos;t apply to foreign foundations described in section
                  4948(b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 8b</span> - If the
                  foundation selected “Yes” to line 7, select whether it
                  furnished a copy of Form 990-PF to the Attorney General (or
                  designate) of each state, as required by General Instruction
                  G. If the answer “No,” attach an explanation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 9</span> - Select “Yes”
                  if the foundation is claiming status as a private operating
                  foundation within the meaning of section 4942(j)(3) or
                  4942(j)(5) for the calendar year 2025 or the tax year
                  beginning in 2025. If selected “Yes,” the foundation must
                  complete Part XII.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 10</span> - Select
                  “Yes” if any persons become substantial contributors during
                  the tax year and attach a schedule listing their names and
                  addresses.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 0,
                        fontSize: '16px !important',
                        lineHeight: 1.3,
                      }}
                    >
                      The term “substantial contributor” means any person whose
                      contributions or bequests, during the current tax year and
                      prior tax years, total more than $5,000 and are more than
                      2% of the total contributions and bequests received by the
                      foundation from its creation through the close of its tax
                      year.
                    </Typography>
                  </Box>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A foreign foundation described in section
                  4948(b) should report only substantial contributors who are
                  U.S. citizens.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVI-A-line11-16-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVI-A-line11-16-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVI-A-line11-16-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VI-A Lines 11–16: foundation entity ownership, donor-advised funds, public inspection, and foreign accounts"
                    width={760}
                    height={226}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 11</span> - Select
                  “Yes,” if at any time during the year the foundation, directly
                  or indirectly, owned a controlled entity within the meaning of
                  section 512(b)(13).
                </Typography>
                <Typography variant="body1">
                  A controlled entity is an entity in which the foundation owns
                  more than 50% of the:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Stock (by vote or value) in a corporation,
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Interest (of profit or capital) in a partnership, or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The beneficial interest of any other entity.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes,” the foundation must attach a schedule listing the
                  name, address, and EIN of each controlled entity and stating
                  whether the controlled entity is an excess business holding.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12</span> - Select
                  “Yes” if the foundation made a distribution to a donor-advised
                  fund over which the foundation or a disqualified person had
                  advisory privileges.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes,” include an attachment stating whether the foundation
                  treated any distribution to a donor-advised fund as a
                  qualifying distribution, and explain how the distributions
                  will be used to accomplish a purpose described in section
                  170(c)(2)(B).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 13</span> - Select
                  “Yes” if the foundation complies with the public inspection
                  requirements for its annual returns and exemption application.
                  If “Yes,” enter the foundation&apos;s website address if the
                  foundation has a website. Otherwise, enter “N/A.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 15</span> - Section
                  4947(a)(1) nonexempt charitable trusts that file Form 990-PF
                  instead of Form 1041 must check and complete this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The trust should include exempt-interest dividends received
                  from a mutual fund or other regulated investment company as
                  well as tax-exempt interest received directly.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 16</span> - Select
                  “Yes” if, at any time during the calendar year 2025, the
                  foundation has an interest in or a signature or other
                  authority over a bank, securities, or other financial account
                  in a foreign country.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” electronically file FinCEN Form 114, Report
                  of Foreign Bank and Financial Accounts (FBAR), with the
                  Department of the Treasury using the FinCEN&apos;s BSA
                  E-Filing System. Because FinCEN Form 114 isn&apos;t a tax
                  form, don&apos;t file it with Form 990-PF.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VI-B - Statements Regarding Activities for Which Form
                  4720 May Be Required
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVI-B-line1-4b-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVI-B-line1-4b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVI-B-line1-4b-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VI-B Lines 1-4b: activities requiring Form 4720, including dealings with disqualified persons, excess business holdings, and risky investments."
                    width={760}
                    height={641}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The purpose of questions in this part is to determine whether
                  any initial excise tax is due under sections 4941–4945,
                  170(f)(10), 4960, and 4965. If the answer is “Yes” to the
                  question on lines 1b, 1c, 2b, 3b, 4a, 4b, 5b, 6b, 7b, or 8,
                  complete and file Form 4720 unless an exception applies.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” or “No” during the year, did the foundation
                  (either directly or indirectly):
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Engage in the sale, exchange, or leasing of property with a
                    disqualified person?
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Borrow money from, lend money to, or otherwise extend credit
                    to (or accept it from) a disqualified person?
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Furnish goods, services, or facilities to (or accept them
                    from) a disqualified person?
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Pay compensation to, or pay or reimburse the expenses of, a
                    disqualified person?
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Transfer any income or assets to a disqualified person (or
                    make any of either available for the benefit or use of a
                    disqualified person)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Agree to pay money or property to a government official?
                    (Exception. Check “No” if the foundation agreed to make a
                    grant to or to employ the official for a period after
                    termination of government service if terminating within 90
                    days.)
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - If your
                  foundation answered “Yes” to any of the questions in line 1a,
                  it should answer “Yes” to line 1b unless all of the acts
                  engaged in were acts excepted by the regulations under section
                  4941 or other guidance, including Notices published in the
                  Internal Revenue Bulletin relating to disaster assistance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1c</span> - Check this
                  line if your foundation is one of the organizations relying on
                  a current notice regarding disaster assistance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 1d</span> - Select
                  “Yes” if the foundation engaged in a prior year in any of the
                  acts described in 1a, other than excepted acts, that were not
                  corrected before the first day of the tax year beginning in
                  2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2a</span> - Select
                  “Yes” if, at the end of the tax year 2025, the foundation has
                  any undistributed income (Part XII, lines 6d and 6e) for the
                  tax year(s) beginning before 2025. If “Yes,” list the years
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2b</span> - Select
                  “Yes” if there are any years listed in 2a for which the
                  foundation is not applying the provisions of section
                  4942(a)(2) (relating to incorrect valuation of assets) to the
                  year&apos;s undistributed income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you answer “No” to the question on line 2b, attach a
                  statement explaining:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    All the facts regarding the incorrect valuation of assets;
                    and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The actions taken (or planned) to comply with section
                    4942(a) (2)(B), (C), and (D) and the related regulations.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Foreign foundations described in section 4948(b) need not
                  complete line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2c</span> - If the
                  provisions of section 4942(a)(2) are being applied to any of
                  the years listed in 2a, list the years here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3a</span> - If “Yes,”
                  did it have excess business holdings in 2025 as a result of
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Any purchase by the foundation or disqualified persons after
                    May 26, 1969;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The lapse of the 5 years (or longer period approved by the
                    Commissioner under section 4943(c)(7)) to dispose of
                    holdings acquired by gift or bequest; or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The lapse of the 10-, 15-, or 20-year first phase holding
                    period?
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use Form 4720, Schedule C, to determine if the foundation had
                  excess business holdings in 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4a</span> - Select
                  “Yes” if the foundation invested during the year any amount in
                  a manner that would jeopardize its charitable purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4b</span> - Select
                  “Yes” if the foundation made any investment in a prior year
                  (but after December 31, 1969) that could jeopardize its
                  charitable purpose that had not been removed from jeopardy
                  before the first day of the tax year beginning in 2025.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVI-B-line5-8-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVI-B-line5-8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVI-B-line5-8-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VI-B Lines 1-4b: activities requiring Form 4720, including dealings with disqualified persons, excess business holdings & risky investments."
                    width={760}
                    height={389}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5a</span> - Select
                  “Yes” or “No” during the year, did the foundation pay or incur
                  any amount to:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Carry on propaganda, or otherwise attempt to influence
                    legislation (section 4945(e))
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Influence the outcome of any specific public election (see
                    section 4955); or to carry on, directly or indirectly, any
                    voter registration drive?
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Provide a grant to an individual for travel, study, or other
                    similar purposes.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Provide a grant to an organization other than a charitable,
                    etc., organization described in section 4945(d)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Provide for any purpose other than religious, charitable,
                    scientific, literary, or educational purposes, or the
                    prevention of cruelty to children or animals?
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5b</span> - If the
                  foundation answered “Yes” to 5a(1)–(5), did any of the
                  transactions fail to qualify under the exceptions described in
                  Regulations section 53.4945 or in a current notice regarding
                  disaster assistance?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5c</span> - Check the
                  checkbox in this line if your foundation is an organization
                  relying on a current notice regarding disaster assistance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5d</span> - If your
                  foundation’s answer is “Yes” to question 5a(4), answer whether
                  the foundation claims exemption from the tax because it
                  maintained expenditure responsibility for the grant.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6a</span> -Select “Yes”
                  if the foundation, during the year, received any funds,
                  directly or indirectly, to pay premiums on a personal benefit
                  contract.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6b</span> - Select
                  “Yes” if, in connection with any transfer of funds to a
                  private foundation, the foundation directly or indirectly pays
                  premiums on any personal benefit contract, or there is an
                  understanding or expectation that any person will directly or
                  indirectly pay these premiums.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the foundation selected “Yes” to 6b, it must file{' '}
                  <b>Form 8870</b>,{' '}
                  <i>
                    Information Return for Transfers Associated With Certain
                    Personal Benefit Contracts
                  </i>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7a</span> - Select
                  “Yes” if the foundation was a party to a prohibited tax
                  shelter transaction (PTST) as described in section 4965(e) at
                  any time during the tax year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> If the answer to this question is “Yes,” the
                  foundation must also file <b>Form 8886-T</b>,{' '}
                  <i>
                    Disclosure by Tax-Exempt Entity Regarding Prohibited Tax
                    Shelter Transactions.
                  </i>
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7b</span> - Select
                  “Yes” if the foundation answered “Yes” to line 7a, and it had
                  net income or received proceeds attributable to the PTST
                  during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the foundation answers “Yes” to both lines 7a and 7b, it
                  may be required to file Form 4720 and pay tax concerning each
                  PTST. The foundation&apos;s managers may also be required to
                  file Form 4720 and pay tax concerning the relevant PTSTs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 8</span> - Select “Yes”
                  if the foundation is subject to the section 4960 tax on
                  payment(s) of more than $1,000,000 in remuneration or excess
                  parachute payment(s) during the year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VII - Information About Officers, Directors, Trustees,
                  Foundation Managers, Highly Paid Employees, and Contractors
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVII-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVII-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVII-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VII showing leadership and employee compensation details."
                    width={760}
                    height={463}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - List the
                  names, addresses, and other information requested for those
                  who were officers, directors, and trustees (or any person who
                  had responsibilities or powers similar to those of officers,
                  directors, or trustees) of the foundation at any time during
                  the year.
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>(a) Name and address</li>
                  <li style={{ marginBottom: 12 }}>
                    (b) Title, and average hours per week devoted to position
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    (c) Compensation (If not paid, enter -0-)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    (d) Contributions to employee benefit plans and deferred
                    compensation
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    (e) Expense account, other allowances
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter zero in columns (c), (d), and (e) if no compensation was
                  paid. Attach a schedule if more space is needed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Enter here
                  the information requested for the five employees (if any) of
                  the foundation (or disregarded entity that the foundation
                  owns) who received the greatest amount of annual compensation
                  over $50,000.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Don&apos;t include employees listed already on line 1. Also,
                  enter the total number of other employees who received more
                  than $50,000 in annual compensation.
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    (a) Name and address of each employee paid more than $50,000
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    (b) Title, and average hours per week devoted to position
                  </li>
                  <li style={{ marginBottom: 12 }}>(c) Compensation</li>
                  <li style={{ marginBottom: 12 }}>
                    (d) Contributions to employee benefit plans and deferred
                    compensation
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    (e) Expense account, other allowances
                  </li>
                </ol>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVII-line3-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVII-line3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVII-line3-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VII, Line 3 detailing compensation paid to top independent contractors."
                    width={760}
                    height={211}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Enter in
                  line 3 the information requested for the five highest-paid
                  independent contractors (if any), whether individuals or
                  professional service corporations or associations, to whom the
                  organization paid more than $50,000 for the year to perform
                  personal services of a professional nature for the
                  organization (for example, attorneys, accountants, and
                  doctors).
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    (a) Name and address of each person paid more than $50,000
                  </li>
                  <li style={{ marginBottom: 12 }}>(b) Type of service</li>
                  <li style={{ marginBottom: 12 }}>(c) Compensation</li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, include the total number of all other independent
                  contractors who received more than $50,000 for the year for
                  performing professional services.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VIII-A - Summary of Direct Charitable Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVIII-line1-4-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVIII-line1-4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVIII-line1-4-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VIII-A, Lines 1–4: Summary of the foundation’s direct charitable activities, including program descriptions, expenses, and related details."
                    width={760}
                    height={240}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part comprises the Summary of Direct Charitable
                  Activities of the foundation for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List the foundation&apos;s <b>four largest programs</b> as
                  measured by the direct and indirect expenses attributable to
                  each that consist of the direct active conduct of charitable
                  activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Whether any expenditure is for the direct active conduct of a
                  charitable activity is determined, generally, by the
                  definitions and special rules of section 4942(j)(3) and the
                  related regulations, which define a private operating
                  foundation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  What to Include
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Include scholarships, grants, or other payments to
                    individuals as part of an active program in which the
                    foundation maintains some significant involvement.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Related administrative expenses should be included.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Include qualified set-asides for direct charitable
                    activities reported on line 3 of Part XI.{' '}
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Include in Part VIII-A amounts paid or set aside to acquire
                    assets used in the direct active conduct of charitable
                    activities.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  What Not to Include
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t include in Part VIII-A any grants or expenses
                    attributable to administering grant programs, such as
                    reviewing grant applications, interviewing or testing
                    applicants, selecting grantees, and reviewing reports
                    relating to the use of the grant funds.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t include any program-related investments
                    (reportable in Part VIII-B) in the description and expense
                    totals.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Don&apos;t include current-year expenditures of amounts
                    previously reported as set-asides in Part VIII-A.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VIII-B - Summary of Program-Related Investments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-partVIIIb-summar-img.webp" />
                  <source srcSet="/assets/images/f990pf-partVIIIb-summar-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-partVIIIb-summar-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part VIII-B summarizing the foundation’s program-related investments made to support its charitable purposes"
                    width={760}
                    height={207}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part comprises the Summary of Program-Related Investments
                  of the foundation for the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report here all program-related investments made in the
                  current tax year. Only report investments made in a prior year
                  if they were still held by the foundation in the current tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 1 and 2</span> - List
                  the two largest program-related investments made by the
                  foundation in 2025, if any, and whether or not the investments
                  were still held by the foundation at the end of the year. If
                  none, enter “NONE.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3</span> - Combine all
                  other program-related investments and enter the total on line
                  3 in the Amount column. List the individual investments or
                  groups of investments included (attach a schedule, if
                  necessary).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The total of lines 1 through 3 in the Amount
                  column must equal the amount reported on line 1b of Part XI.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part IX - Minimum Investment Return
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part9-minimum-img.webp" />
                  <source srcSet="/assets/images/f990pf-part9-minimum-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part9-minimum-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part IX Lines 1–6 showing the minimum investment return calculation."
                    width={760}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All domestic foundations must complete Part IX of Form 990-PF
                  return.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Fair market value of assets not used (or held for
                  use) directly in carrying out charitable, etc., purposes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - Enter the
                  average monthly fair market value of securities in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - Include the
                  average of all cash balances and amounts that may be used for
                  charitable purposes or set aside and taken as a qualifying
                  distribution.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1c</span> - Report here
                  the value of all assets other than charitable-use assets,
                  publicly traded securities, cash, and certain “excluded
                  assets” described in Regulations section 53.4942(a)-2(c)(2).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1d</span> - Add lines
                  1a, 1b, and 1c and enter the sum in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 1e</span> - Enter the
                  reduction amount claimed for blockage or other factors
                  reported on lines 1a and 1c. Also, provide an explanation that
                  includes the following information for each asset or group of
                  assets involved.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Enter the
                  total acquisition indebtedness that applies to assets included
                  in line 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Subtract
                  line 2 from line 1d and enter the difference in line 3.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Cash deemed
                  held for charitable activities. Enter 1.5% (0.015) of line 3.
                  If you use a larger amount, attach an explanation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Subtract
                  line 4 from line 3 and enter the difference in line 5. This
                  will be the Net value of noncharitable-use assets.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Minimum Investment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter 5% (0.05) of line 5 (Net value of noncharitable-use
                  assets)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the foundation&apos;s tax period is less than 12 months,
                  determine the applicable percentage by dividing the number of
                  days in the short tax period by 365 (or 366 in a leap year).
                  Multiply the result by 5% (0.05). Then multiply the modified
                  percentage by the amount on line 5 and enter the result on
                  line 6.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part X - Distributable Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part10-distribut-img.webp" />
                  <source srcSet="/assets/images/f990pf-part10-distribut-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part10-distribut-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part X Lines 1–7 Distributable Amount"
                    width={760}
                    height={178}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization is claiming status as a private operating
                  foundation described in section 4942(j)(3) or (j)(5) or if it
                  is a foreign foundation that checked Item D2 in the Heading
                  section on page 1, check the box in the Heading section for
                  Part X.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You don&apos;t need to complete this part.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - Enter in
                  this line the minimum investment return value from Part IX,
                  line 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2a</span> - Enter here
                  the tax on investment income for 2025 from Part V, line 5.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2b</span> - Enter the
                  income tax amount for 2025. (This must not include the tax
                  from Part V.)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2c</span> - Add lines
                  2a and 2b and enter the sum amount in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Subtract
                  line 2c from line 1 and enter the difference in line 3. This
                  amount will be the distributable amount before adjustments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Enter here
                  the total of recoveries of amounts treated as qualifying
                  distributions for any year under section 4942(g).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Include recoveries of part or all (as applicable) of grants
                  previously made, proceeds from the sale or other disposition
                  of property whose cost was treated as a qualifying
                  distribution when the property was acquired, and any amount
                  set aside under section 4942(g) to the extent it is determined
                  that this amount isn&apos;t necessary for the set-aside.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Add lines 3
                  and 4 and enter the sum amount in this line.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Deduction from distributable amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the foundation was organized before May 27, 1969, and its
                  governing instrument or any other instrument continues to
                  require the accumulation of income after a judicial proceeding
                  according to section 508(e) to reform the instrument has
                  terminated, then the income required to be accumulated must be
                  subtracted from the distributable amount beginning with the
                  first tax year after the tax year in which the judicial
                  proceeding was terminated.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7 - Distributable amount as adjusted
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 6 from line 5 and enter the difference here and
                  on Part XII, line 1.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XI - Qualifying Distributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part11-line1-4-img.webp" />
                  <source srcSet="/assets/images/f990pf-part11-line1-4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part11-line1-4-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XI Lines 1–4 Qualifying Distributions"
                    width={760}
                    height={178}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Qualifying distributions</b> are amounts spent or set aside
                  for religious, educational, or charitable purposes. Foreign
                  foundations described in section 4948(b) not claiming
                  operating foundation status need not complete this part.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Amounts paid (including administrative expenses) to
                  accomplish charitable, etc., purposes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> -{' '}
                  <b>Expenses, contributions, gifts, etc</b>. Enter the amount
                  from Part I, line 26, column (d).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> -{' '}
                  <b> Program-related investments</b>. Enter the total of the
                  Amount column from Part VIII-B.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Enter the
                  amounts paid to acquire assets used (or held for use) directly
                  in carrying out charitable, etc., purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Amounts set
                  aside may be treated as qualifying distributions only if the
                  private foundation establishes to the satisfaction of the IRS
                  that the amount will be paid for the specific project within
                  60 months from the date of the first set-aside and meets (1)
                  or (2) below.
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    A set-aside can better accomplish the project than the
                    immediate payment of funds (suitability test).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The private foundation meets the requirements of section
                    4942(g)(2)(B)(ii) (cash distribution test).
                  </li>
                </ol>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4 - Qualifying distributions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add lines 1a through 3b, and enter the sum in line 4 and on
                  Part XII, line 4.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XII - Undistributed Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part12-line1-5-img.webp" />
                  <source srcSet="/assets/images/f990pf-part12-line1-5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part12-line1-5-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XII Lines 1–5 Undistributed Income "
                    width={760}
                    height={422}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  The purpose of this part is to enable the foundation to comply
                  with the rules for applying its qualifying distributions for
                  the year 2025. In applying the qualifying distributions, there
                  are three basic steps.
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Reduce any undistributed income for 2024 (but not below
                    zero).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The organization may use any part of or all remaining
                    qualifying distributions for 2025 to satisfy elections. For
                    example, if undistributed income remained for any year
                    before 2024, it could be reduced to zero or, if the
                    foundation wished, the distributions could be treated as
                    distributions out of corpus.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If no elections are involved, apply the remaining qualifying
                    distributions to the 2025 distributable amount on line 4d.
                    If the remaining qualifying distributions are greater than
                    the 2025 distributable amount, the excess is treated as a
                    distribution out of corpus on line 4e.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your foundation checked Item D2 in the Heading section on
                  page 1, don&apos;t fill in this part.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - Enter in
                  this line the distributable amount for 2025 from Part X, line
                  7.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2a</span> - Enter here
                  the distributable amount for 2024 and amounts for earlier
                  years that remained undistributed at the end of the 2025 tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2b</span> - Enter the
                  amount of undistributed income for years before 2024.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - If your
                  foundation has made excess distributions out of corpus in
                  prior years, which have yet to be applied in any year, enter
                  the amount for each year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don&apos;t enter an amount for a particular year if
                  the organization was a private operating foundation for any
                  later year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3a through 3e</span> -
                  Enter the amount of any excess distribution made on the line
                  for each year listed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Don&apos;t include any amount that was applied against the
                  distributable amount of an earlier year or that was already
                  used to meet pass-through distribution requirements.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3f</span> - Add lines
                  3a through e and enter the sum in this line. This amount can
                  be applied in 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 4</span> - Enter the
                  total amount of qualifying distributions made in 2025 from
                  Part XI, line 4, on the line next to column (a). The total of
                  the amounts applied on lines 4a through 4e is equal to the
                  qualifying distributions made in 2025
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 4a</span> - The
                  qualifying distributions for 2025 are first used to reduce any
                  undistributed income remaining from 2024.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter only enough of the 2025 qualifying distributions to
                  reduce the 2024 undistributed income to zero.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 4b and 4c</span> - If
                  there are any 2025 qualifying distributions remaining after
                  reducing the 2024 undistributed income to zero, one or more
                  elections can be made under Regulations section
                  53.4942(a)-3(d)(2) to apply all or part of the remaining
                  qualifying distributions to any undistributed income remaining
                  from years before 2024 or to apply to the corpus.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Where to enter.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    If the organization elected to apply all or part of the
                    remaining amount to the undistributed income remaining from
                    years before 2024 enter the amount on line 4b.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If the organization elected to treat those qualifying
                    distributions as a distribution out of corpus, enter the
                    amount on line 4c.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4d</span> - In this
                  line, enter only enough of the remaining 2025 qualifying
                  distributions to reduce the 2025 distributable amount to zero.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4e</span> - Any 2025
                  qualifying distributions remaining after reducing the 2025
                  distributable amount to zero should be treated as an excess
                  distribution out of corpus. This amount may be carried over
                  and applied to later years.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5 - Excess qualifying distributions carryover applied to
                  2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any excess qualifying distributions from line 3, which
                  were applied to 2025, in both the Corpus column and the 2025
                  column.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Apply the oldest excess qualifying distributions first. Thus,
                  the organization will apply any excess qualifying
                  distributions carried forward from 2019 before those from
                  later years.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part12-line6-10-img.webp" />
                  <source srcSet="/assets/images/f990pf-part12-line6-10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part12-line6-10-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XII Lines 6–10 Undistributed Income"
                    width={760}
                    height={506}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6a</span> - Add lines
                  3f, 4c, and 4e. Subtract line 5 from the total. Enter the net
                  total in the Corpus column.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6b</span> - Prior
                  years’ undistributed income. Subtract line 4b from line 2b and
                  enter the amount in the <b>Years before 2024</b> column.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6c</span> - Enter only
                  the undistributed income from 2023 and prior years for which
                  either a notice of deficiency under section 6212(a) has been
                  mailed for the section 4942(a) first-tier tax, or on which the
                  first-tier tax has been assessed because the organization
                  filed a Form 4720 for a tax year that began before 2024.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 6d and 6e</span> -
                  These amounts are taxable under the provisions of section
                  4942(a), except for any part that is due solely to improper
                  valuation of assets to which the provisions of section
                  4942(a)(2) are being applied. Report the taxable amount on
                  Form 4720. If the exception applies, attach an explanation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 6f</span> - Subtract
                  lines 4d and 5 from line 1 and enter the value in line 6f.
                  This amount must be distributed in 2026
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 7</span> - Enter here
                  the total distributions out of corpus made to satisfy the
                  restrictions on amounts received from donors.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8 - Outdated excess distributions carryover.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Because of the 5-year carryover limitation under section
                  4942(i)(2), the organization must reduce any excess
                  distributions carryover by any amounts from 2019 that
                  weren&apos;t applied in 2025.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Excess distributions carryover to 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount by which line 6a is more than the total of
                  lines 7 and 8. This is the amount the organization may apply
                  to in 2026 and the following years. Line 9 can never be less
                  than zero.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Analysis of line 9.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In the space provided for each year, enter the amount of
                  excess distributions carryover from that year that hasn&apos;t
                  been applied as of the end of the 2025 tax year. If there is
                  an amount on the line for 2021, it must be applied by the end
                  of the 2025 tax year since the 5-year carryover period for
                  2021 ends in 2026.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XIII - Private Operating Foundations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part13-line1-3-img.webp" />
                  <source srcSet="/assets/images/f990pf-part13-line1-3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part13-line1-3-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XIII Lines 1-3 Private Operating Foundations"
                    width={760}
                    height={573}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All organizations that claim status as private operating
                  foundations under section 4942(j)(3) or (5) for 2025 must
                  complete this Part.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your foundation is a section 4942(j)(5) organization,
                  complete only lines 1a, 1b, 2c, 2d, 2e, and 3b. Enter “N/A” on
                  all other lines in the Total column for Part XIII.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 1a</span> - If the
                  foundation has received a ruling or determination letter that
                  it is a private operating foundation, and the ruling is
                  effective for 2025, enter the ruling date in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 1b</span> - In this
                  line, check the box to indicate whether the foundation is a
                  private operating foundation described in section 4942(j)(3)
                  or 4942(j)(5)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2a</span> - Enter the
                  lesser of the adjusted net income from Part I or the minimum
                  investment return from Part IX for the current tax year and
                  the prior 3 years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2b</span> - Calculate
                  85% (0.85) of the value from line 2a and enter those values in
                  line 2b.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2c</span> - Enter in
                  this line the value of Qualifying distributions from Part XI,
                  line 4, for each year listed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2d</span> - Enter here
                  the amounts included in line 2c not used directly for the
                  active conduct of exempt activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2e</span> - Subtract
                  line 2d from line 2c. These will be the qualifying
                  distributions made directly for the active conduct of exempt
                  activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3</span> - Complete
                  lines 3a, b, or c for the alternative test relied upon. There
                  a 3 such tests
                </Typography>
                <ol style={{ marginBottom: 16 }}>
                  <li style={{ marginBottom: 12 }}>
                    Assets test. 65% or more of the foundation&apos;s assets are
                    devoted directly to those activities or functionally related
                    businesses, or both, or 65% or more of the foundation&apos;s
                    assets are stock of a corporation that is controlled by the
                    foundation, and substantially all of the assets of the
                    corporation are devoted to those activities or functionally
                    related businesses.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Endowment test. The foundation normally makes qualifying
                    distributions directly for the active conduct of the exempt
                    purpose or functions for which it is organized and operated
                    in an amount that is two-thirds or more of its minimum
                    investment return.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Support test. The foundation normally receives 85% or more
                    of its support (other than gross investment income as
                    defined in section 509(e)) from the public and from five or
                    more exempt organizations that aren&apos;t described in
                    section 4946(a)(1) (H) concerning each other or the
                    recipient foundation. Not more than 25% of the support
                    (other than gross investment income) normally may be
                    received from any one of the exempt organizations and not
                    more than one-half of the support normally may be received
                    from gross investment income.
                  </li>
                </ol>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  A new private foundation must use the aggregation method to
                  satisfy the tests for its first tax year to be treated as a
                  private operating foundation from the beginning of that year.
                  It must continue to use the aggregation method for its second
                  and third tax years to maintain its status for those years.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XIV Supplementary Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part14-line1-2d-img.webp" />
                  <source srcSet="/assets/images/f990pf-part14-line1-2d-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part14-line1-2d-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XIV Lines 1-2d Showing Supplementary Information"
                    width={760}
                    height={373}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The foundation must complete this part only if it has $5,000
                  or more in assets at any time during the year. This part
                  doesn&apos;t apply to a foreign foundation that during its
                  entire period of existence received substantially all (85% or
                  more) of its support (other than gross investment income) from
                  sources outside the United States.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Information Regarding Foundation Managers
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - List here
                  the details of any managers of the foundation who have
                  contributed more than 2% of the total contributions received
                  by the foundation before the close of any tax year (but only
                  if they have contributed more than $5,000)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - List here
                  the details of any managers of the foundation who own 10% or
                  more of the stock of a corporation (or an equally large
                  portion of the ownership of a partnership or other entity) in
                  which the foundation has a 10% or greater interest.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Information Regarding Contribution, Grant, Gift,
                  Loan, Scholarship, etc., Programs
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the foundation only contributes to preselected charitable
                  organizations and doesn&apos;t accept unsolicited applications
                  for funds, check the box on line 2 and complete items 2a, b,
                  c, and d.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Line 2a</b> - The name, address, and telephone number or
                    email address of the person to whom applications should be
                    addressed.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Line 2b</b> - The form in which applications should be
                    submitted and the information and materials they should
                    include.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Line 2c</b> - Any submission deadlines
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Line 2d</b> - Any restrictions or limitations on awards,
                    such as by geographical areas, charitable fields, kinds of
                    institutions, or other factors.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This information benefits the grant seeker and the foundation.
                  The grant seekers will be aware of the grant eligibility
                  requirements, and the foundation should receive only
                  applications that adhere to these grant application
                  requirements.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3 - Grants and Contributions Paid During the Year or
                  Approved for Future Payment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part14-line3a-img.webp" />
                  <source srcSet="/assets/images/f990pf-part14-line3a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part14-line3a-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XIV Line 3a – Grants and contributions paid or approved."
                    width={760}
                    height={635}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3a</span> -{' '}
                  <b>Paid during the year</b>. List here all the contributions,
                  grants, etc., actually paid during the year, including grants
                  or contributions that aren&apos;t qualifying distributions
                  under section 4942(g). Include current-year payments of
                  set-asides treated as qualifying distributions in the current
                  tax year or any prior year.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part14-line3b-img.webp" />
                  <source srcSet="/assets/images/f990pf-part14-line3b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part14-line3b-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XIV Line 3a – Grants and contributions paid or approved."
                    width={760}
                    height={293}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3b</span> -{' '}
                  <b>Approved for future payment</b>. List here all
                  contributions, grants, etc., approved during the year but not
                  paid by the end of the year, including the unpaid portion of
                  any current year set aside. Don&apos;t report contributions
                  and grants approved or set aside in a prior tax year but still
                  unpaid as of the end of the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XV-A: Analysis of Income-Producing Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part15a-analysis-img.webp" />
                  <source srcSet="/assets/images/f990pf-part15a-analysis-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part15a-analysis-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XV-A Line 1-13 – Analysis of income – producing Activities "
                    width={760}
                    height={507}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part XV-A explains how a tax-exempt organization earns its
                  income and whether each income source is taxable, excluded, or
                  related to the organization’s exempt purpose. The information
                  reported in this section must align with amounts already shown
                  in Part I of Form 990-PF.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section plays a key role in determining whether the
                  organization has unrelated business income (UBI) and whether
                  Form 990-T may be required.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column a - Business Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the six-digit business activity code from the Form 990-T
                  instructions that best describes the activity generating the
                  income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column b - Unrelated Business Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income earned from activities that are Regularly
                  carried on and not substantially related to the organization’s
                  exempt purpose (Internal Revenue Code section 512).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column c - Exclusion Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If income is excluded from unrelated business income, Enter
                  the appropriate exclusion code. If multiple codes apply, use
                  the lowest numbered code. When multiple income sources are
                  reported on one line, use the code that applies to the largest
                  revenue source
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column d - Excluded Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income excluded from unrelated business taxable income
                  under: IRC sections 512, 513, or 514
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column e- Related or Exempt Function Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report income that:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Is directly related to the organization’s exempt purpose, or
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Is excluded from gross income under other tax provisions (for
                  example, tax-exempt bond interest under section 103)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Any amount reported in column (e) must be explained in Part
                  XV-B.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1 - Program Service Revenue (Lines 1a–1g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report income from activities that directly support the
                  organization’s exempt purpose. <br /> Each revenue-producing
                  program service should be listed separately.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 1g includes payments for services provided to government
                  agencies.
                  <br /> Government grants should not be reported here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The total of lines 1a–1g must match Part I, line 11.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2- Membership Dues and Assessments
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report dues or assessments paid by members.
                  <br /> Classify the income based on whether it relates to
                  exempt activities or provides unrelated benefits.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3 - Interest on Savings and Temporary Cash Investments
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Report interest earned from:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Bank accounts
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Certificates of deposit
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Short-term investments
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This income is generally excluded from unrelated business
                  income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4 -Dividends and Interest From Securities
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Report dividends and interest earned from:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Stocks
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Bonds
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Mutual funds
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These amounts are typically excluded unless derived from
                  debt-financed investments.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5 - Rental Income (or Loss) From Real Estate
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 5a - Rental income from debt-financed property
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 5b - Rental income from property not debt-financed
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Amounts reported on line 5b must match Part I, line 5b.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6 - Net Rental Income (or Loss) From Personal Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report income from renting personal property such as:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Equipment
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Vehicles
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Other non-real estate assets
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This income is often treated as unrelated business income
                  unless an exclusion applies.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7 - Other Investment Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report investment income not included elsewhere, such as:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Royalties
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Certain partnership income
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8 - Gain or (Loss) From Sale of Assets Other Than
                  Inventory
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report gains or losses from the sale of:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Securities
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Land or buildings
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Equipment
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  These amounts are generally excluded unless the asset is
                  debt-financed.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9 -Net Income (or Loss) From Special Events
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report net income from fundraising events such as:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Dinners
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Auctions
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Charity events
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Related expenses already deducted in Part I must be taken into
                  account.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10 - Gross Profit (or Loss) From Sales of Inventory
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report profit or loss from selling goods, including:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Merchandise
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Publications
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Products
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This income is often unrelated unless closely connected to the
                  exempt purpose.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11 - Other Revenue (Lines 11a–11e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Report income not included on lines 1–10.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The total must match Part I, line 11.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12 -Subtotal
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the totals from columns (b), (d), and (e).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13 -Total Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Enter the total of all income classifications.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This amount should be reconciled with Part I, line 12, column
                  (a) using the IRS worksheet provided in the instructions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XV-B – Relationship of Activities to the Accomplishment
                  of Exempt Purposes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part15b-relationship-img.webp" />
                  <source srcSet="/assets/images/f990pf-part15b-relationship-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part15b-relationship-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XV-B Relationship of Activities to the Accomplishment of Exempt Purposes"
                    width={760}
                    height={440}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use this part to explain how each amount reported in{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part XV-A, column (e)
                  </span>{' '}
                  is related to or qualifies as exempt-function income. For each
                  amount, reference the{' '}
                  <span style={{ fontWeight: 600 }}>
                    line number from Part XV-A
                  </span>{' '}
                  and briefly describe how the activity itself directly
                  contributes to accomplishing the organization’s exempt
                  purpose, rather than merely generating funds. Also explain any
                  income in column (e) that is excluded from gross income under
                  tax provisions other than sections 512, 513, or 514 (such as
                  tax-exempt interest). If no amounts are reported in column (e)
                  of Part XV-A, this part does not need to be completed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XVI: Information Regarding Transfers to and Transactions
                  and Relationships With Noncharitable Exempt Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990pf-part16-information-img.webp" />
                  <source srcSet="/assets/images/f990pf-part16-information-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990pf-part16-information-img.png"
                    loading="lazy"
                    alt="Form 990-PF Part XVI – Transfers, transactions, and relationships with noncharitable exempt organizations."
                    width={760}
                    height={725}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part XVI of Form 990-PF asks about your foundation’s
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    transactions and relationships with noncharitable exempt
                    organizations
                  </span>{' '}
                  (like certain nonprofits or political organizations). The goal
                  is transparency—so the IRS knows if your foundation is giving,
                  selling, or sharing resources with other noncharitable
                  organizations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1 – Reporting Certain Transfers and Transactions
                </Typography>
                <Typography variant="body1">
                  Report{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    any transfer or transaction
                  </span>{' '}
                  between the reporting organization and a noncharitable exempt
                  organization.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Related organizations:
                    </span>{' '}
                    Report{' '}
                    <span style={{ fontWeight: 600 }}>
                      all direct or indirect transfers and transactions,
                    </span>{' '}
                    except contributions or grants received from the
                    noncharitable exempt organization.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Unrelated organizations:
                    </span>{' '}
                  </li>
                  <ul>
                    <li>
                      Transfers must be reported on{' '}
                      <span style={{ fontWeight: 600 }}>line 1a.</span>{' '}
                    </li>
                    <li>
                      Transactions must be reported on{' '}
                      <span style={{ fontWeight: 600 }}>line 1b</span> if they
                      do not meet the exceptions (less than $500 with adequate
                      consideration).{' '}
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a – Transfers
                </Typography>
                <Typography variant="body1">
                  A “transfer” is any{' '}
                  <span style={{ fontWeight: 600 }}>value given</span> (cash,
                  property, services, or use of assets){' '}
                  <span style={{ fontWeight: 600 }}>
                    without receiving something of more than nominal value.
                  </span>
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter “Yes” if any direct or indirect transfers are made to
                    a noncharitable exempt organization.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Examples:</span>{' '}
                    Contributions, gifts, grants.{' '}
                  </li>
                  <li>
                    Do <span style={{ fontWeight: 600 }}>not</span> include only
                    transfers made{' '}
                    <span style={{ fontWeight: 600 }}>
                      to the reporting organization
                    </span>{' '}
                    from the other organization; those are excluded.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b – Other Transactions
                </Typography>
                <Typography variant="body1">
                  Report transactions in which the reporting organization
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    received less than adequate consideration,
                  </span>{' '}
                  or as otherwise described in sublines 1b(1)–1b(6).
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Line 1b(1)–1b(6):</span>{' '}
                    Report all types of transactions, including:{' '}
                  </li>
                  <ul>
                    <li>Sales or purchases of goods or services </li>
                    <li>Property transfers </li>
                    <li>Leasing arrangements (lessor or lessee) </li>
                    <li>Expense reimbursements </li>
                    <li>Loans or loan guarantees </li>
                    <li>
                      Services, membership, or fundraising performed for the
                      other organization{' '}
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Unrelated organizations exception:
                  </span>{' '}
                  If the transaction was for adequate consideration{' '}
                  <span style={{ fontWeight: 600 }}>
                    and the amount involved is $500 or less,
                  </span>{' '}
                  answer “No.”
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c – Facilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the use of facilities or property provided to or
                  received from a noncharitable exempt organization, including
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    office space, land, buildings, or structures,
                  </span>{' '}
                  regardless of ownership or lease.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d – Description of Transfers and Transactions
                </Typography>
                <Typography variant="body1">
                  Describe each transfer or transaction for which “Yes” was
                  indicated on lines 1a–1c.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (a):</span> Enter
                    the line number from 1a–1c (for example, “1b(3)”).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (d):</span>{' '}
                    Provide a brief description. If more space is needed, enter{' '}
                    <span style={{ fontWeight: 600 }}>“See Attached”</span> and
                    include the details on a separate sheet.{' '}
                  </li>
                  <li>
                    Combine all cash transfers to a single organization into{' '}
                    <span style={{ fontWeight: 600 }}>one entry</span>;
                    otherwise, list each transfer or transaction
                    separately.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2 – Reporting Certain Relationships
                </Typography>
                <Typography variant="body1">
                  Report each{' '}
                  <span style={{ fontWeight: 600 }}>
                    noncharitable exempt organization that is related or
                    affiliated
                  </span>{' '}
                  with the reporting organization.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Do not list unrelated organizations. </li>
                  <li>
                    Include organizations even if the relationship or control
                    factor exists{' '}
                    <span style={{ fontWeight: 600 }}>
                      only part of the year
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Column a - </span>Line
                  number from 1a–1c for the transfer or transaction (used in
                  line 1d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Column b - </span>Exempt
                  category of the organization (for line 2, e.g., 501(c)(4))
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Column c - </span>
                  Description of the relationship (for line 2)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Column d - </span>
                  Description of the transfer or transaction (for line 1d); use
                  “See Attached” if more space is needed
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2a – Related Noncharitable Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check <span style={{ fontWeight: 600 }}>Yes</span> if your
                  foundation is connected (directly or indirectly) to
                  non-charitable tax-exempt organizations (not 501(c)(3)) or
                  political organizations (527). Check{' '}
                  <span style={{ fontWeight: 600 }}>No</span> if there’s no
                  connection.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2b – List Related Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you answered Yes, fill in the table:
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Column a - </span>Enter the
                  Name of organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Column b - </span>Enter
                  your Type of organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Column c - </span>
                  Description of the relationship
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 990-PF?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  You have two options to file your{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    form 990 pf
                  </Link>{' '}
                  return with the IRS - efficient e-filing or traditional paper
                  filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Form 990-PF E-filing (recommended)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The IRS wants you to e-file! It&apos;s <b>quicker</b>,{' '}
                  <b>simpler</b>, and <b>more secure</b> than paper filing.
                  Plus, you get instant confirmation that your return is
                  received, saving you time and stress.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Choose <b>TaxZerone</b>, an IRS-authorized e-file provider, to
                  make your Form 990-PF e-filing process a breeze. Our
                  user-friendly platform lets you complete your return in just a
                  few clicks.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Focus on what matters most: Your foundation&apos;s mission.
                  Let TaxZerone handle the Form 990-PF filing hassle. Get
                  started today and experience a smooth, efficient filing
                  process.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form 990, here are the steps you
                  need to follow
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b> Obtain the Form:</b> Download and print Form 990-PF from
                    the IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Complete Form:</b> Fill out each section of Form 990-PF,
                    providing accurate information about your foundation’s
                    missions, programs, finances, and compliance with tax laws.
                    Use the instructions provided by the IRS to guide you
                    through the process.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Attach Schedules if Necessary:</b> Depending on your
                    foundation’s activities and financial transactions, you may
                    need to attach additional schedules or supporting documents
                    to Form 990-PF
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Calculate and Include Payment: </b> If your organization
                    owes taxes based on the information provided on Form 990-PF,
                    calculate the amount owed and include payment with the
                    submission. Payments can be made by check or money order
                    payable to the &quot;United States Treasury.&quot;
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Sign and Date the Form: </b> The form must be signed and
                    dated by an authorized individual within the foundation,
                    such as the president, treasurer, or another authorized
                    officer.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Mail the Form: </b> Once completed, mail the signed Form
                    990-PF and any accompanying documents to the IRS mailing
                    address.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Keep Copies for Your Records: </b> Make copies of the
                    completed Form 990-PF and all supporting documents for your
                    organization&apos;s records. These documents may be needed
                    for future reference or in the event of an audit.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> While the IRS previously allowed paper filing
                  for Form 990-PF, there has been a change in recent years. As
                  for tax years ending after July 31, 2020, electronic filing is
                  mandatory for all Form 990-PF submissions.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 990-PF?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you choose paper filing, the mailing address for Form
                  990-PF depends on your foundation&apos;s location.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Send a paper copy of Form 990-PF to the mailing address
                  mentioned below:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Department of the Treasury,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Internal Revenue Service Center,
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Ogden, UT 84201-0027.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  If your foundation’s principal business, office, or agency is
                  located in a foreign country or a U.S. possession, mail the
                  form to:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Internal Revenue Service Center,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  P.O. Box 409101,
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Ogden, UT 84409.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 990-PF?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing your Form 990-PF takes just 3 steps! To make it even
                  faster, gather all the necessary information (like your
                  foundation details, and program activities) before you start.
                  That way, you can complete your entire 990-PF filing process
                  in no time.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Information required to file Form 990-PF
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Foundation&apos;s details, EIN, tax year, accounting method,
                  and exempt status. Once you have this information ready, you
                  can follow the steps below to e-file Form 990-PF using
                  TaxZerone.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1</b> - Enter the required information, such as your
                  foundation’s details, EIN, tax year, accounting method, and
                  exempt status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2</b> - Complete all the parts provided in the form
                  and attach the required schedules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 3</b> - Review the information you provided in the
                  form, check and rectify the errors if any, and transmit the
                  Form 990-PF return to the IRS.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  File Your Form 990-PF Effortlessly with TaxZerone.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When you choose to file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    irs form 990 pf
                  </Link>{' '}
                  with TaxZerone, you can enjoy benefits such as
                </Typography>

                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Seamless and Secure:</b> TaxZerone takes the complexity
                      out of filing your Form 990-PF. Our{' '}
                      <span style={{ fontWeight: 600 }}>IRS-authorized</span>{' '}
                      service ensures a smooth and secure experience, so you can
                      focus on what matters most.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Intuitive Design:</b> TaxZerone boasts a user-friendly
                      interface. Effortlessly navigate and complete your 990-PF
                      return in minutes.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Built-in Accuracy: </b>Experience the peace of mind
                      that comes with built-in IRS form validations. Catch and
                      rectify any errors before submission, ensuring a flawless
                      filing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Unparalleled Security: </b> Your data is precious.
                      TaxZerone utilizes industry-leading security protocols to
                      keep your Form 990-PF return safe throughout the entire
                      process.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Remarkable Value: </b> TaxZerone offers the best value
                      in the industry, starting at just{' '}
                      <Hidden smDown>
                        {' '}
                        <br />
                      </Hidden>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Just $149.99 per return
                      </span>
                      . Experience exceptional service at a price you&apos;ll
                      love.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Start filing smarter today. Choose <b>TaxZerone</b>.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Meet IRS Requirements and Keep Your Tax-Exempt Status
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file Form 990 with TaxZerone—efficient, secure, and built to
              support your mission.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990PFinstructions`}
              >
                E-file Form 990-PF
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990PFInstructions;
